import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, gql } from "@apollo/client";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  useColorMode,
  Link,
  Divider,
  Image,
  Circle,
  Spinner,
  Center,
  useToast,
  SimpleGrid,
  Flex,
} from "@chakra-ui/react";
import { SEOHead } from "../seometadata/components/SEOHead";

// Countdown timer hook
const useCountdown = (targetDate: Date) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = targetDate.getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
};

// GraphQL Queries and Mutations
const GET_BDAY_GUEST = gql`
  query BdayGuestBySlug($slug: String!) {
    bdayGuestBySlug(slug: $slug) {
      id
      firstName
      lastName
      rsvpStatus
      plusOnes
      plusOneNames
      rsvpRespondedAt
    }
  }
`;

const SUBMIT_RSVP = gql`
  mutation SubmitBdayRsvp($slug: String!, $input: RsvpInput!) {
    submitBdayRsvp(slug: $slug, input: $input) {
      id
      firstName
      rsvpStatus
      plusOnes
      rsvpRespondedAt
    }
  }
`;

type RsvpStatus = "PENDING" | "ATTENDING" | "NOT_ATTENDING" | "MAYBE";

// Party date constant - defined outside component to avoid re-creation on each render
const PARTY_DATE = new Date('2026-03-14T14:00:00+10:00');

const BdayRsvpPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { colorMode } = useColorMode();
  const toast = useToast();

  const [selectedStatus, setSelectedStatus] = useState<RsvpStatus | null>(null);
  const [plusOnes, setPlusOnes] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Countdown to the party - March 14, 2026 at 2pm AEST
  const countdown = useCountdown(PARTY_DATE);

  // Elegant boutique colour palette
  const champagne = "#F7E7CE";
  const softGold = "#D4AF37";
  const warmWhite = "#FAF9F6";
  const deepCharcoal = "#1C1C1C";

  const bgGradient = colorMode === 'light'
    ? `linear-gradient(180deg, ${warmWhite} 0%, ${champagne}40 100%)`
    : `linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)`;

  const cardBg = colorMode === 'light'
    ? "rgba(255, 255, 255, 1)"
    : "rgba(30, 30, 46, 1)";

  const textPrimary = colorMode === 'light' ? deepCharcoal : "#F5F5F5";
  const textSecondary = colorMode === 'light' ? "#666666" : "#B0B0B0";
  const accentColor = colorMode === 'light' ? softGold : "#E8D5A3";

  const { data, loading, error } = useQuery(GET_BDAY_GUEST, {
    variables: { slug },
    skip: !slug,
  });

  const [submitRsvp, { loading: submitting }] = useMutation(SUBMIT_RSVP, {
    onCompleted: () => {
      setHasSubmitted(true);
      toast({
        title: "RSVP Submitted!",
        description: "Thanks for letting us know!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (err) => {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const guest = data?.bdayGuestBySlug;

  // Set initial values if guest has already responded
  useEffect(() => {
    if (guest?.rsvpStatus && guest.rsvpStatus !== "PENDING") {
      setSelectedStatus(guest.rsvpStatus);
      setPlusOnes(guest.plusOnes || 0);
      if (guest.rsvpRespondedAt) {
        setHasSubmitted(true);
      }
    }
  }, [guest]);

  const handleSubmit = () => {
    if (!selectedStatus || !slug) return;

    submitRsvp({
      variables: {
        slug,
        input: {
          rsvpStatus: selectedStatus,
          plusOnes: selectedStatus === "ATTENDING" ? plusOnes : 0,
        },
      },
    });
  };

  // Loading state
  if (loading) {
    return (
      <Box bg={bgGradient} minHeight="100vh">
        <Center h="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color={accentColor} thickness="3px" />
            <Text color={textSecondary}>Loading your invite...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  // Not found state
  if (error || !guest) {
    return (
      <Box bg={bgGradient} minHeight="100vh">
        <Center h="100vh">
          <VStack spacing={4}>
            <Text fontSize="6xl">🎂</Text>
            <Heading size="lg" color={textPrimary}>Invite not found</Heading>
            <Text color={textSecondary}>This invite link doesn't seem to be valid.</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box
      bg={bgGradient}
      minHeight="100vh"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: colorMode === 'light'
          ? `radial-gradient(60% 60% at 80% 20%, ${champagne}40 0%, transparent 50%)`
          : `radial-gradient(60% 60% at 80% 20%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)`,
        pointerEvents: 'none',
        zIndex: 0,
      }}
      sx={{
        '@keyframes borderShimmer': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' }
        },
        '@keyframes gentleBounce': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-3px)' }
        },
        '@keyframes sparkle': {
          '0%': { transform: 'translateX(-200px) translateY(-100px)', opacity: 0 },
          '5%': { opacity: 0.4 },
          '25%': { transform: 'translateX(25vw) translateY(25vh)', opacity: 0.8 },
          '50%': { transform: 'translateX(50vw) translateY(50vh)', opacity: 1 },
          '75%': { transform: 'translateX(75vw) translateY(75vh)', opacity: 0.8 },
          '95%': { opacity: 0.4 },
          '100%': { transform: 'translateX(calc(100vw + 200px)) translateY(100vh)', opacity: 0 }
        },
        '@keyframes sparkle-slow': {
          '0%': { transform: 'translateX(-250px) translateY(-100px)', opacity: 0 },
          '5%': { opacity: 0.3 },
          '30%': { transform: 'translateX(35vw) translateY(30vh)', opacity: 0.6 },
          '60%': { transform: 'translateX(65vw) translateY(60vh)', opacity: 0.8 },
          '95%': { opacity: 0.3 },
          '100%': { transform: 'translateX(calc(100vw + 250px)) translateY(100vh)', opacity: 0 }
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-10%',
          width: '120%',
          height: '100%',
          backgroundImage: colorMode === 'light'
            ? `
              radial-gradient(3px 3px at 10% 10%, rgba(212,175,55,0.6) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 30%, rgba(212,175,55,0.5) 50%, transparent 50%),
              radial-gradient(2px 2px at 15% 50%, rgba(212,175,55,0.7) 50%, transparent 50%),
              radial-gradient(5px 5px at 25% 20%, rgba(212,175,55,0.4) 50%, transparent 50%),
              radial-gradient(3px 3px at 30% 60%, rgba(212,175,55,0.6) 50%, transparent 50%),
              radial-gradient(6px 6px at 35% 40%, rgba(212,175,55,0.3) 50%, transparent 50%),
              radial-gradient(4px 4px at 40% 70%, rgba(212,175,55,0.5) 50%, transparent 50%),
              radial-gradient(2px 2px at 45% 15%, rgba(212,175,55,0.8) 50%, transparent 50%),
              radial-gradient(5px 5px at 50% 80%, rgba(212,175,55,0.4) 50%, transparent 50%)
            `
            : `
              radial-gradient(3px 3px at 10% 10%, rgba(232,213,163,0.5) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 30%, rgba(232,213,163,0.4) 50%, transparent 50%),
              radial-gradient(2px 2px at 15% 50%, rgba(232,213,163,0.6) 50%, transparent 50%),
              radial-gradient(5px 5px at 25% 20%, rgba(232,213,163,0.3) 50%, transparent 50%),
              radial-gradient(3px 3px at 30% 60%, rgba(232,213,163,0.5) 50%, transparent 50%),
              radial-gradient(6px 6px at 35% 40%, rgba(232,213,163,0.25) 50%, transparent 50%),
              radial-gradient(4px 4px at 40% 70%, rgba(232,213,163,0.4) 50%, transparent 50%),
              radial-gradient(2px 2px at 45% 15%, rgba(232,213,163,0.7) 50%, transparent 50%),
              radial-gradient(5px 5px at 50% 80%, rgba(232,213,163,0.35) 50%, transparent 50%)
            `,
          backgroundSize: '400px 400px, 500px 500px, 350px 350px, 450px 450px, 550px 550px, 600px 600px, 480px 480px, 420px 420px, 520px 520px',
          backgroundPosition: '0 0, 80px 60px, 160px 40px, 240px 100px, 320px 160px, 400px 80px, 480px 200px, 560px 20px, 640px 180px',
          animation: 'sparkle 25s linear infinite',
          pointerEvents: 'none',
          zIndex: 1,
        }
      }}
    >
      <SEOHead
        pagePath={`/40years/${slug}`}
        fallbackTitle={`${guest.firstName}'s Invite - Tom's 40th`}
        fallbackDescription={`${guest.firstName} is invited to Tom's 40th Birthday. A poolside celebration on Saturday 14th March 2026.`}
      />

      {/* Secondary sparkle layer */}
      <Box
        position="absolute"
        top={0}
        left="-20%"
        width="140%"
        height="100%"
        opacity={0.5}
        pointerEvents="none"
        zIndex={1}
        sx={{
          backgroundImage: colorMode === 'light'
            ? `
              radial-gradient(5px 5px at 5% 15%, rgba(212,175,55,0.4) 50%, transparent 50%),
              radial-gradient(3px 3px at 15% 35%, rgba(212,175,55,0.5) 50%, transparent 50%),
              radial-gradient(7px 7px at 10% 55%, rgba(212,175,55,0.3) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 25%, rgba(212,175,55,0.6) 50%, transparent 50%),
              radial-gradient(8px 8px at 25% 45%, rgba(212,175,55,0.25) 50%, transparent 50%),
              radial-gradient(6px 6px at 30% 65%, rgba(212,175,55,0.45) 50%, transparent 50%),
              radial-gradient(3px 3px at 35% 20%, rgba(212,175,55,0.7) 50%, transparent 50%)
            `
            : `
              radial-gradient(5px 5px at 5% 15%, rgba(232,213,163,0.35) 50%, transparent 50%),
              radial-gradient(3px 3px at 15% 35%, rgba(232,213,163,0.4) 50%, transparent 50%),
              radial-gradient(7px 7px at 10% 55%, rgba(232,213,163,0.25) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 25%, rgba(232,213,163,0.5) 50%, transparent 50%),
              radial-gradient(8px 8px at 25% 45%, rgba(232,213,163,0.2) 50%, transparent 50%),
              radial-gradient(6px 6px at 30% 65%, rgba(232,213,163,0.4) 50%, transparent 50%),
              radial-gradient(3px 3px at 35% 20%, rgba(232,213,163,0.6) 50%, transparent 50%)
            `,
          backgroundSize: '600px 600px, 700px 700px, 550px 550px, 650px 650px, 750px 750px, 680px 680px, 580px 580px',
          backgroundPosition: '0 0, 120px 140px, 240px 80px, 360px 200px, 480px 280px, 600px 120px, 720px 40px',
          animation: 'sparkle-slow 35s linear infinite',
        }}
      />

      <Container maxW="container.sm" py={{ base: 8, md: 16 }} px={{ base: 4, md: 8 }} position="relative" zIndex={2}>
        <VStack spacing={0} align="stretch">

          {/* Main Invitation Card with rotating shimmer border */}
          <Box
            position="relative"
            borderRadius="3xl"
            p="1.5px"
            overflow="hidden"
            shadow="2xl"
          >
            {/* Rotating gradient border - larger arc to reach all edges */}
            <Box
              position="absolute"
              top="-50%"
              left="-50%"
              width="200%"
              height="200%"
              bg={`conic-gradient(from 0deg, transparent 0deg, transparent 30deg, ${accentColor} 70deg, ${accentColor} 110deg, transparent 150deg, transparent 360deg)`}
              animation="borderShimmer 4s linear infinite"
              sx={{
                transformOrigin: 'center center',
              }}
            />
            {/* Static subtle border underneath */}
            <Box
              position="absolute"
              inset={0}
              borderRadius="3xl"
              bg={colorMode === 'light' ? "rgba(212, 175, 55, 0.15)" : "rgba(232, 213, 163, 0.1)"}
            />
            {/* Card content */}
            <Box
              bg={cardBg}
              borderRadius="calc(1.5rem - 1.5px)"
              position="relative"
              zIndex={1}
            >
            <Box py={{ base: 10, md: 14 }} px={{ base: 6, md: 10 }}>
              <VStack spacing={6}>

                {/* Personalized Greeting */}
                <VStack spacing={2}>
                  <Text
                    fontSize="sm"
                    textTransform="uppercase"
                    letterSpacing="0.3em"
                    color={textSecondary}
                  >
                    Hey
                  </Text>
                  <Heading
                    as="h1"
                    size={{ base: "xl", md: "2xl" }}
                    color={accentColor}
                    textAlign="center"
                    fontWeight="300"
                  >
                    {guest.firstName}!
                  </Heading>
                </VStack>

                {/* Photo */}
                <Circle
                  size={{ base: "120px", md: "150px" }}
                  overflow="hidden"
                  border="3px solid"
                  borderColor={accentColor}
                  bg={colorMode === 'light' ? "gray.100" : "gray.700"}
                >
                  <Image
                    src="/bday/truck_photo.png"
                    alt="Tom"
                    objectFit="cover"
                    w="100%"
                    h="100%"
                  />
                </Circle>

                {/* The Big Number */}
                <VStack spacing={1}>
                  <Text
                    fontSize="sm"
                    fontStyle="italic"
                    color={textSecondary}
                  >
                    You're invited to celebrate
                  </Text>
                  <Text
                    fontSize={{ base: "5xl", md: "6xl" }}
                    fontWeight="200"
                    color={accentColor}
                    lineHeight="1"
                  >
                    40
                  </Text>
                  <Text
                    fontSize="xs"
                    textTransform="uppercase"
                    letterSpacing="0.3em"
                    color={textSecondary}
                  >
                    Years Young
                  </Text>
                </VStack>

                <Divider w="60px" borderColor={accentColor} opacity={0.5} />

                {/* Event Details */}
                <VStack spacing={1}>
                  <Text
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="300"
                    color={textPrimary}
                  >
                    Saturday 14th March 2026
                  </Text>
                  <Text fontSize="md" color={textSecondary}>
                    2pm to 6pm
                  </Text>
                  <Text fontSize="md" color={textSecondary}>
                    11 Athlone Street, Woolloongabba
                  </Text>
                </VStack>

                {/* Event Description */}
                <Text
                  fontSize="md"
                  color={textSecondary}
                  textAlign="center"
                  maxW="280px"
                  lineHeight="1.6"
                >
                  Join us for a relaxed poolside afternoon with drinks, BBQ, and good times.
                </Text>

                {/* Countdown Timer */}
                <Box
                  py={4}
                  px={{ base: 4, md: 6 }}
                  borderRadius="2xl"
                  bg={colorMode === 'light' ? `${champagne}30` : 'rgba(212, 175, 55, 0.1)'}
                  border="1px solid"
                  borderColor={`${accentColor}40`}
                  w="full"
                  maxW="340px"
                >
                  <Text
                    fontSize="xs"
                    textTransform="uppercase"
                    letterSpacing="0.2em"
                    color={textSecondary}
                    textAlign="center"
                    mb={3}
                  >
                    Countdown to Lift Off
                  </Text>
                  <SimpleGrid columns={4} spacing={{ base: 2, md: 4 }}>
                    {[
                      { value: countdown.days, label: 'Days' },
                      { value: countdown.hours, label: 'Hours' },
                      { value: countdown.minutes, label: 'Mins' },
                      { value: countdown.seconds, label: 'Secs' },
                    ].map((item, index) => (
                      <VStack key={index} spacing={0}>
                        <Box
                          bg={colorMode === 'light' ? 'white' : 'rgba(255,255,255,0.1)'}
                          borderRadius="lg"
                          px={{ base: 2, md: 3 }}
                          py={{ base: 2, md: 3 }}
                          minW={{ base: "50px", md: "60px" }}
                          shadow="sm"
                          border="1px solid"
                          borderColor={`${accentColor}30`}
                        >
                          <Text
                            fontSize={{ base: "xl", md: "2xl" }}
                            fontWeight="300"
                            color={accentColor}
                            textAlign="center"
                            fontFamily="mono"
                          >
                            {String(item.value).padStart(2, '0')}
                          </Text>
                        </Box>
                        <Text
                          fontSize={{ base: "2xs", md: "xs" }}
                          color={textSecondary}
                          textTransform="uppercase"
                          letterSpacing="0.1em"
                          mt={1}
                        >
                          {item.label}
                        </Text>
                      </VStack>
                    ))}
                  </SimpleGrid>
                </Box>

                <Divider w="60px" borderColor={accentColor} opacity={0.5} />

                {/* RSVP Section */}
                {hasSubmitted && selectedStatus ? (
                  // Already responded view
                  <VStack spacing={4} py={4}>
                    <Text
                      fontSize="sm"
                      textTransform="uppercase"
                      letterSpacing="0.2em"
                      color={textSecondary}
                    >
                      Your Response
                    </Text>
                    <Box
                      bg={selectedStatus === "ATTENDING" ? "green.100" : selectedStatus === "NOT_ATTENDING" ? "red.100" : "yellow.100"}
                      color={selectedStatus === "ATTENDING" ? "green.700" : selectedStatus === "NOT_ATTENDING" ? "red.700" : "yellow.700"}
                      px={6}
                      py={3}
                      borderRadius="full"
                      fontWeight="500"
                    >
                      {selectedStatus === "ATTENDING" && `Yes! ${plusOnes > 0 ? `+${plusOnes}` : ""}`}
                      {selectedStatus === "NOT_ATTENDING" && "Can't make it"}
                      {selectedStatus === "MAYBE" && "Maybe"}
                    </Box>
                    <Button
                      variant="ghost"
                      size="sm"
                      color={textSecondary}
                      onClick={() => setHasSubmitted(false)}
                    >
                      Change response
                    </Button>
                  </VStack>
                ) : (
                  // RSVP form
                  <VStack spacing={6} py={4} w="full">
                    <Text
                      fontSize="sm"
                      textTransform="uppercase"
                      letterSpacing="0.2em"
                      color={textSecondary}
                    >
                      Will you be joining us?
                    </Text>

                    <Flex
                      direction={{ base: "column", sm: "row" }}
                      gap={3}
                      w="full"
                      justify="center"
                      align="center"
                    >
                      <Button
                        size="lg"
                        variant={selectedStatus === "ATTENDING" ? "solid" : "outline"}
                        colorScheme="green"
                        onClick={() => setSelectedStatus("ATTENDING")}
                        borderRadius="full"
                        px={8}
                        w={{ base: "full", sm: "auto" }}
                        maxW={{ base: "200px", sm: "none" }}
                        animation={selectedStatus !== "ATTENDING" ? "gentleBounce 2s ease-in-out infinite" : "none"}
                        _hover={{
                          animation: "none",
                          transform: "translateY(-2px)",
                        }}
                      >
                        Yes!
                      </Button>
                      <Button
                        size="lg"
                        variant={selectedStatus === "NOT_ATTENDING" ? "solid" : "outline"}
                        colorScheme="red"
                        onClick={() => setSelectedStatus("NOT_ATTENDING")}
                        borderRadius="full"
                        px={8}
                        w={{ base: "full", sm: "auto" }}
                        maxW={{ base: "200px", sm: "none" }}
                      >
                        Can't make it
                      </Button>
                      <Button
                        size="lg"
                        variant={selectedStatus === "MAYBE" ? "solid" : "outline"}
                        colorScheme="yellow"
                        onClick={() => setSelectedStatus("MAYBE")}
                        borderRadius="full"
                        px={8}
                        w={{ base: "full", sm: "auto" }}
                        maxW={{ base: "200px", sm: "none" }}
                      >
                        Maybe
                      </Button>
                    </Flex>

                    {/* Plus ones - only show if attending */}
                    {selectedStatus === "ATTENDING" && (
                      <VStack spacing={3}>
                        <Text fontSize="sm" color={textSecondary}>
                          Bringing anyone?
                        </Text>
                        <Flex gap={2} wrap="wrap" justify="center">
                          {[0, 1, 2, 3].map((num) => (
                            <Button
                              key={num}
                              size={{ base: "sm", md: "md" }}
                              variant={plusOnes === num ? "solid" : "outline"}
                              bg={plusOnes === num ? accentColor : "transparent"}
                              color={plusOnes === num ? "white" : textSecondary}
                              borderColor={accentColor}
                              onClick={() => setPlusOnes(num)}
                              borderRadius="full"
                              minW={{ base: "60px", md: "70px" }}
                              _hover={{
                                bg: plusOnes === num ? accentColor : `${accentColor}20`,
                              }}
                            >
                              {num === 0 ? "Just me" : `+${num}`}
                            </Button>
                          ))}
                        </Flex>
                      </VStack>
                    )}

                    {/* Submit button */}
                    {selectedStatus && (
                      <Button
                        size="lg"
                        bg={accentColor}
                        color="white"
                        onClick={handleSubmit}
                        isLoading={submitting}
                        borderRadius="full"
                        px={10}
                        _hover={{
                          bg: colorMode === 'light' ? "#c9a030" : "#d4c090",
                          transform: "translateY(-2px)",
                        }}
                        transition="all 0.2s"
                      >
                        Submit RSVP
                      </Button>
                    )}
                  </VStack>
                )}

              </VStack>
            </Box>
            </Box>
          </Box>

          {/* Footer */}
          <HStack justify="center" pt={6} spacing={4} opacity={0.6}>
            <Text fontSize="lg">🏊</Text>
            <Text fontSize="lg">☀️</Text>
            <Text fontSize="lg">👨‍👩‍👧‍👦</Text>
            <Text fontSize="lg">🥂</Text>
          </HStack>
          <Text
            textAlign="center"
            fontSize="xs"
            color={textSecondary}
            pt={1}
            letterSpacing="0.1em"
            textTransform="uppercase"
          >
            Family welcome · No pressure to swim
          </Text>

        </VStack>
      </Container>
    </Box>
  );
};

export default BdayRsvpPage;
